import classnames from 'classnames';

import variations from './variations/index';
import singlePostIcon from './icons';
import './editor.scss';
import { title } from './block.json';
import { InspectorTabs } from '@components/inspector-tabs';
import { getBlockClassNames } from '@components/class-dropdown';
import { getPosts, decodeUnicode } from '@helpers/post-block-utils';
import { DEFAULT_CSS_CLASS } from '@constants';
import { FocusableIcon } from '@components/focusable-block-icon';
import { InspectorGeneral, InspectorAppearance } from './settings';

const {
  i18n: { __ },
  element: { useState, useEffect, useMemo },
  blockEditor: {
    useBlockProps,
    useInnerBlocksProps,
    BlockContextProvider,
    __experimentalBlockVariationPicker: BlockVariationPicker,
    store: blockEditorStore,
  },
  data: { useSelect, useDispatch, withSelect },
  components: { Notice },
  hooks: { applyFilters },
  blocks: { createBlocksFromInnerBlocksTemplate },
} = wp;

const VariationPicker = ({ clientId, setAttributes }) => {
  const { replaceInnerBlocks } = useDispatch(blockEditorStore);
  // when skip, first variant will be set as a default
  const defaultVariation = variations[0];
  return (
    <BlockVariationPicker
      variations={variations}
      label={__('Single Post', 'b3rg')}
      instructions={__('Choose a variation to start with.', 'b3rg')}
      icon={singlePostIcon}
      onSelect={(variation = defaultVariation) => {
        if (variation.attributes) {
          setAttributes(variation.attributes);
        }
        if (variation.innerBlocks) {
          replaceInnerBlocks(
            clientId,
            createBlocksFromInnerBlocksTemplate(variation.innerBlocks),
            true,
          );
        }
      }}
      allowSkip
    />
  );
};

const EditorSettings = (props) => {
  const {
    attributes,
    setAttributes,
    clientId,
    currentPostId,
    innerBlocks,
    featuredImgUrl,
  } = props;

  const {
    blockVisibility,
    blockClassNames,
    selectedPostType,
    selectedPost,
    isBackgroundImage,
    anchorAppearance,
    template,
    dateFormat,
    fancyBoxClasses,
    enableFocusableIcon,
  } = attributes;

  const [postSelectIsLoading, setPostSelectIsLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [showMetaDataFormatInput, setShowMetaDataFormatInput] = useState(false);
  const [placeholderImageUrl, setPlaceholderImageUrl] = useState('');

  const dateRelatedMetaFields = applyFilters(
    'b3rg_single_post_date_related_meta_fields',
    ['event_start_date', 'event_end_date', 'custom_date', 'event_start_end'],
  );

  const checkMetaFieldBlockIsDateRelated = (block) => {
    if (block.innerBlocks.length) {
      block.innerBlocks.forEach(checkMetaFieldBlockIsDateRelated);
    } else if (block.name === 'b3rg/meta-field') {
      const {
        attributes: { selectedMetaField },
      } = block;

      if (dateRelatedMetaFields.includes(selectedMetaField)) {
        setShowMetaDataFormatInput(true);
      }
    }
  };

  //update showMetaDataFormatInput value, only selected meta field is date related field
  useEffect(() => {
    setShowMetaDataFormatInput(false);
    innerBlocks.forEach(checkMetaFieldBlockIsDateRelated);
  }, [innerBlocks]); //eslint-disable-line

  const setPlaceHolderImageUrl = (placeHolderImage) => {
    //overwrite placeholder image from theme side
    const placeholderImageUrlPassByTheme = applyFilters(
      'b3rg_replace_placeholder_image',
      placeholderImageUrl,
    );

    return placeHolderImage ? placeHolderImage : placeholderImageUrlPassByTheme;
  };

  const setPostsData = (postList) => {
    const postsOptions = [];
    const postIds = [];
    postList.forEach((post) => {
      const {
        id,
        title: { rendered },
      } = post;

      if (currentPostId !== id && rendered) {
        postsOptions.push({
          value: id,
          label: decodeUnicode(rendered),
        });
        postIds.push(id);
      }

      if (id === parseInt(selectedPost.value)) {
        const {
          featured_media: featuredMedia,
          meta: { place_holder_image_url: postPlaceHolderImageUrl },
        } = post;
        const updatedPlaceholderImageUrl = setPlaceHolderImageUrl(
          postPlaceHolderImageUrl,
        );
        setAttributes({ selectedFeaturedImgId: featuredMedia });
        setPlaceholderImageUrl(updatedPlaceholderImageUrl);
      }
    });

    setPostSelectIsLoading(false);
    setPosts(postsOptions);

    if (
      selectedPost.value !== undefined &&
      !postIds.includes(parseInt(selectedPost.value))
    ) {
      setAttributes({ selectedPost: '' });
    }
  };

  useEffect(() => {
    setPostSelectIsLoading(true);
    const query = {
      per_page: -1,
      order: 'asc',
      orderby: 'title',
      status: 'publish',
    };
    getPosts(selectedPostType, setPostsData, query);

    const enableMediaLibraryInserter = applyFilters(
      'b3rg_single_post_template',
      template,
    );

    if (enableMediaLibraryInserter) {
      setAttributes({ template: enableMediaLibraryInserter });
    }
  }, [selectedPost.value, selectedPostType]); // eslint-disable-line

  // set block classes and set to blockProps
  const singlePostClasses = useMemo(
    () => getBlockClassNames(blockClassNames),
    [blockClassNames],
  );

  const blockProps = useBlockProps({
    className: classnames({
      [`${DEFAULT_CSS_CLASS.SINGLE_POST} ${DEFAULT_CSS_CLASS.SINGLE_POST}_${clientId} ${singlePostClasses}`]: true,
      disable: !blockVisibility,
      'no-image': !featuredImgUrl,
      'bg-img': isBackgroundImage,
      'has-focusable-icon': enableFocusableIcon,
    }),
  });

  const { children, ...innerBlocksProps } = useInnerBlocksProps(blockProps, {
    template,
  });

  const generalTabProps = {
    ...props,
    showMetaDataFormatInput,
    postSelectIsLoading,
    posts,
    innerBlocks,
  };

  return (
    <>
      <InspectorTabs>
        <InspectorGeneral {...generalTabProps} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      <div {...innerBlocksProps}>
        {enableFocusableIcon && (
          <FocusableIcon {...{ title, icon: singlePostIcon }} />
        )}
        {selectedPostType && selectedPost.value ? (
          <BlockContextProvider
            value={{
              postId: selectedPost.value,
              postType: selectedPostType,
              dateFormat,
              anchorAppearance,
              fancyBoxClasses,
            }}
          >
            <div className={`${DEFAULT_CSS_CLASS.SINGLE_POST}__inner`}>
              {children}
            </div>
          </BlockContextProvider>
        ) : (
          <Notice status="info" isDismissible={false}>
            {__('Please select a post from the sidebar settings.', 'b3rg')}
          </Notice>
        )}
      </div>
    </>
  );
};

const DisplayBlocks = withSelect((select, props) => {
  const {
    clientId,
    attributes: { selectedFeaturedImgId },
  } = props;

  return {
    currentPostId: select('core/editor').getCurrentPostId(),
    innerBlocks: select('core/editor').getBlocks(clientId),
    featuredImgUrl: select('core').getMedia(selectedFeaturedImgId)?.source_url,
  };
})(EditorSettings);

const Edit = (props) => {
  const {
    attributes: { isRelatedPost },
    clientId,
  } = props;
  const hasInnerBlocks = useSelect(
    (select) => select(blockEditorStore).getBlocks(clientId).length > 0,
    [clientId],
  );
  const Component =
    hasInnerBlocks || isRelatedPost ? DisplayBlocks : VariationPicker;

  return <Component {...props} />;
};

export default Edit;
